import { format, isValid } from "date-fns";
import { useCallback, useMemo } from "react";

// store
import useStore from "../../../../store";

import {
	useCohorts,
	useNPSAdditionalFeedback,
	usePageSettings,
	useQuestionsSummary,
} from "../../../../../../api/hooks";

import { getEngagementSurveyQuestionAnswers } from "../../../../../../api/services";

import downloadCSV from "../../../../../../utils/downloadCSV";
import escapeCsvField from "../../../../../../utils/escapeCsvField";

// config
import Config from "../../../../Config";

export type DistributionData = {
	total_response_count: number;
	score_to_count: Record<1 | 2 | 3 | 4 | 5, number>;
};

export type QuestionRow = {
	id: string;
	question_id: string;
	factor: string;
	question_text: string;
	average_score: number | null;
	distribution: DistributionData;
	total_response_count: number;
	scale: string;
	type: string;
	included_in_satisfaction: boolean;
	enabled: boolean;
};

const { DEFAULT_COHORT_SELECTION, DEFAULT_FACTOR_SELECTION } = Config.QUESTIONS.TABLE;

export const scoresToPercentages = (distributionData: DistributionData): Record<1 | 2 | 3 | 4 | 5, number> => {
	const { total_response_count, score_to_count } = distributionData;
	return Object.entries(score_to_count).reduce(
		(acc, [score, count]) => {
			acc[score] = (count / total_response_count) * 100;
			return acc;
		},
		{ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
	);
};

/**
 * Hook to make an api call based on changes the engagement date range filter state
 * and return rows formatted for the QuestionsTable component
 */
const useQuestionTableRows = (
	searchTerm: string
): {
	rows: QuestionRow[];
	isLoading: boolean;
	getCSVContent: () => string | undefined;
	selectCSVDownloadName: () => string;
	handleDownloadQuestionAnswers: (questionId: string) => void;
	getRowInformation: (questionId: string) => {
		questionId: string;
		filter: {
			startDate: Date | null;
			endDate: Date | null;
			value: string;
		};
		cohortId: string;
	};
	handleDownloadNPSAdditionalFeedback: () => void;
} => {
	const [filter] = useStore((state) => [state.filter]);
	const { data: pageSettingsData } = usePageSettings();
	const { data: cohortData } = useCohorts();
	const selectedCohort = pageSettingsData?.questions?.allQuestionsTable?.cohort || DEFAULT_COHORT_SELECTION;
	const selectedFactor = pageSettingsData?.questions?.allQuestionsTable?.factor || DEFAULT_FACTOR_SELECTION;

	const cohortName = useMemo(() => {
		const cohortOptions = [{ _id: "all", name: "All Employees" }, ...(cohortData?.cohorts || [])];
		return cohortOptions.find((c) => c._id === selectedCohort)?.name;
	}, [selectedCohort, cohortData]);

	const { data: questionsData, isLoading } = useQuestionsSummary({
		startDate: filter.startDate,
		endDate: filter.endDate,
		cohortIds: selectedCohort !== "all" ? selectedCohort : undefined,
		factor: selectedFactor !== "All Factors" ? selectedFactor : undefined,
		search: searchTerm,
	});

	const { data: npsAdditionalFeedbackData } = useNPSAdditionalFeedback({
		startDate: filter.startDate,
		endDate: filter.endDate,
		cohortIds: selectedCohort !== "all" ? selectedCohort : undefined,
	});

	const questionRows: QuestionRow[] = useMemo(() => {
		return (
			questionsData?.data.map((question) => ({
				id: question._id,
				question_id: question._id,
				factor: question.factor,
				question_text: question.question_text,
				average_score: question.average_score ? parseFloat(question.average_score.toFixed(1)) : null,
				distribution: question.distribution as DistributionData,
				total_response_count: question.distribution.total_response_count ?? 0,
				scale: question.scale,
				type: question.type || "Core",
				included_in_satisfaction: question.included_in_satisfaction ?? true,
				enabled: question.enabled,
			})) || []
		);
	}, [questionsData]);

	const selectCSVDownloadName = useCallback(() => {
		const date = new Date().toDateString();
		const timePeriod = Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label;
		const middleText = `${cohortName}${searchTerm.length ? `-Search By '${searchTerm}'` : ""}`;

		return `PerformYard-Engagement-Questions-${timePeriod}-${middleText}-${date}.csv`;
	}, [cohortName, searchTerm, filter.value]);

	const getCSVContent = () => {
		if (!questionsData) return;
		const csvColumns = [
			"Type",
			"Factor",
			"Question Text",
			"# of Responses",
			"Average Score",
			"Strongly Disagree (1)",
			"Disagree (2)",
			"Neither Agree nor Disagree (3)",
			"Agree (4)",
			"Strongly Agree (5)",
		];
		const csvRows = questionsData.data.map((question) => {
			const scoresMap = scoresToPercentages(question.distribution as DistributionData);
			return [
				question.type || "Core",
				escapeCsvField(question.factor),
				escapeCsvField(question.question_text),
				question.distribution.total_response_count,
				question.average_score ? parseFloat(question.average_score.toFixed(1)) : "",
				...[1, 2, 3, 4, 5].map((score) => {
					if (scoresMap[score] === 0) return "";
					// round up to 1% if less than 1%
					const wholeNumberPercentage = Math.max(Math.round(scoresMap[score]), 1);
					return `${wholeNumberPercentage}%`;
				}),
			];
		});
		return [csvColumns, ...csvRows].map((row) => row.join(",")).join("\n");
	};

	const handleDownloadQuestionAnswers = async (questionId) => {
		const { data = [] } = await getEngagementSurveyQuestionAnswers(
			questionId,
			filter.startDate || undefined,
			filter.endDate || undefined,
			selectedCohort !== "all" ? selectedCohort : undefined
		);
		const questionText = questionsData?.data?.find((question) => question._id === questionId)?.question_text || "";
		const cohortMap = cohortData?.cohorts?.reduce((acc, cohort) => {
			acc[cohort._id] = cohort.name;
			return acc;
		}, {});
		const csvColumns = ["Question Text", "Answer", "Cohorts"].join(",") + "\n";

		const csvContent = data
			.filter((row) => row.value !== "")
			.map((row) => {
				const questionTextEscaped = escapeCsvField(questionText);
				const valueEscaped = escapeCsvField(String(row.value));
				const cohortsEscaped = escapeCsvField(
					String(row.cohort_ids.map((cohortId) => cohortMap?.[cohortId] || "").join("; "))
				);
				return `${questionTextEscaped},${valueEscaped},${cohortsEscaped}`;
			})
			.join("\n");

		const date = new Date().toDateString();
		const timePeriod = Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label || "";

		const filename = `PerformYard-Engagement-Question-${questionText}-${timePeriod}-${cohortName}-${date}.csv`;

		downloadCSV(csvColumns + csvContent, filename);
	};

	const getRowInformation = (questionId) => {
		return {
			questionId,
			filter: {
				startDate: filter.startDate,
				endDate: filter.endDate,
				value: filter.value,
			},
			cohortId: selectedCohort,
		};
	};

	const handleDownloadNPSAdditionalFeedback = useCallback(() => {
		const additionalFeedback = npsAdditionalFeedbackData?.data;
		const cohortMap = cohortData?.cohorts?.reduce(
			(acc, cohort) => {
				acc[cohort._id] = cohort.name;
				return acc;
			},
			{} as Record<string, string>
		);

		const getCSVContent = () => {
			const csvColumns = ["eNPS Score", "Additional Feedback", "Cohorts", "Response Date"];

			const csvRows = additionalFeedback?.map((feedback) => {
				const submittedAtDate = new Date(feedback.submitted_at);
				let formattedSubmittedAtDate = "";
				if (isValid(submittedAtDate)) {
					formattedSubmittedAtDate = format(submittedAtDate, "dd-MMM-yyyy");
				} else {
					formattedSubmittedAtDate = "";
				}
				const cohorts = escapeCsvField(
					(feedback.cohort_ids || [])
						.map((cohortId) => cohortMap?.[cohortId] || "")
						.filter(Boolean)
						.sort((a, b) => a.localeCompare(b))
						.join("; ")
				);
				return [
					feedback.enps_value,
					escapeCsvField(feedback.additional_feedback),
					cohorts,
					formattedSubmittedAtDate,
				];
			}) || [["", "", "", ""]];

			return [csvColumns, ...csvRows].join("\n");
		};

		const date = new Date().toDateString();
		const timePeriod =
			Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label ||
			(filter.startDate && filter.endDate
				? filter.startDate.toDateString() + " to " + filter.endDate.toDateString()
				: "");
		const filename = `PerformYard-Engagement-eNPS-AdditionalFeedback-${timePeriod}-${cohortName}-${date}.csv`;
		const csvContent = getCSVContent();

		downloadCSV(csvContent, filename);
	}, [npsAdditionalFeedbackData, filter.value, filter.startDate, filter.endDate, cohortName, cohortData]);

	return {
		rows: questionRows,
		isLoading,
		getCSVContent,
		selectCSVDownloadName,
		handleDownloadQuestionAnswers,
		getRowInformation,
		handleDownloadNPSAdditionalFeedback,
	};
};

export default useQuestionTableRows;
